import { mapState } from "vuex";

export const ENINT_CATEGORY_KEY = "enIntCategory";

/** tb_enint_master.dataInputType — 서버와 동일 */
export const ENINT_DATA_INPUT_NEW = "NEW_POINT";
export const ENINT_DATA_INPUT_EXISTING = "EXISTING_POINT";

/** getMasterSummary / getDataList 조회 */
export const ENINT_DATE_TYPE_YEAR = "YEAR";
export const ENINT_DATE_TYPE_MONTH = "MONTH";

/** 증감률·비교 % (양수·플러스) 표시색 */
export const EI_DELTA_UP_COLOR = "#b91c1c";

/** 증감률·비교 % (음수·마이너스) 표시색 */
export const EI_DELTA_DOWN_COLOR = "#0f766e";

/**
 * 루트 요소 `:style`에 바인딩해 scoped CSS에서 `var(--ei-delta-up)` / `var(--ei-delta-down)` 로 사용.
 * @type {Readonly<Record<string, string>>}
 */
export const EI_DELTA_COLOR_CSS_VARS = Object.freeze({
    "--ei-delta-up": EI_DELTA_UP_COLOR,
    "--ei-delta-down": EI_DELTA_DOWN_COLOR,
});

/**
 * getCalcList·recalcRange 등 레거시 API용: dateType+dateValue → 닫힌 YM 구간 (UI 조회 모델 아님).
 * MONTH: 포커스 월이 속한 연도의 1~12월 전체 (getCalcList가 한 달만 조회되지 않도록).
 * @returns {{ fromYm: number, toYm: number } | null}
 */
export function calcListBoundsFromDatePeriod(dateType, dateValue) {
    const dt = String(dateType || "").toUpperCase();
    const dv = Number(dateValue);
    if (dt === ENINT_DATE_TYPE_YEAR) {
        if (!Number.isFinite(dv) || dv < 2000 || dv > 2100) return null;
        return { fromYm: dv * 100 + 1, toYm: dv * 100 + 12 };
    }
    if (dt === ENINT_DATE_TYPE_MONTH) {
        const y = Math.floor(dv / 100);
        const m = dv % 100;
        if (!Number.isFinite(dv) || m < 1 || m > 12 || y < 2000 || y > 2100) return null;
        return { fromYm: y * 100 + 1, toYm: y * 100 + 12 };
    }
    return null;
}

/** 브릿지 제목 등: 조회 구간의 캘린더 연도 */
export function yearFromDatePeriod(dateType, dateValue) {
    const dt = String(dateType || "").toUpperCase();
    const dv = Number(dateValue);
    if (dt === ENINT_DATE_TYPE_YEAR) {
        if (!Number.isFinite(dv) || dv < 2000 || dv > 2100) return null;
        return dv;
    }
    if (dt === ENINT_DATE_TYPE_MONTH) {
        const y = Math.floor(dv / 100);
        if (!Number.isFinite(dv) || y < 2000 || y > 2100) return null;
        return y;
    }
    return null;
}

export function isValidDatePeriod(dateType, dateValue) {
    return calcListBoundsFromDatePeriod(dateType, dateValue) != null;
}

export function mapEnIntCategoryState() {
    return mapState({
        enIntCategoryItems: (state) => state.commonCodes?.[ENINT_CATEGORY_KEY] || [],
    });
}

export function categoryLabelFromStoreItem(item) {
    if (!item) return "";
    const value = item.value != null ? String(item.value) : "";
    const text = item.text != null ? String(item.text) : "";
    if (text && value) return `${text} (${value})`;
    return value || text || "";
}

export function categoryNameByValue(items, value) {
    const v = value != null ? String(value) : "";
    const it = (items || []).find((x) => String(x.value) === v);
    return it?.text || "";
}

export function ensureCommonCodes(store) {
    const list = store?.state?.commonCodes?.[ENINT_CATEGORY_KEY];
    if (Array.isArray(list) && list.length) return Promise.resolve();
    return store.dispatch("FETCH_COMMON_CODES");
}

export function ymFromMonthInput(val) {
    if (!val || typeof val !== "string") return null;
    const p = val.split("-");
    if (p.length < 2) return null;
    const y = parseInt(p[0], 10);
    const m = parseInt(p[1], 10);
    if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
    return y * 100 + m;
}

export function formatYm(ym) {
    if (ym == null || ym === "") return "—";
    const s = String(ym);
    if (s.length !== 6) return s;
    return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
}

/** YYYYMM 정수에 월 더하기 */
export function ymAddMonths(ym, delta) {
    const y = Math.floor(ym / 100);
    const m = ym % 100;
    const d = new Date(y, m - 1 + delta, 1);
    return d.getFullYear() * 100 + d.getMonth() + 1;
}

/**
 * 조회기간 정규화 (YYYYMM)
 * @returns {{ fromYm: number, toYm: number } | null}
 */
export function normalizeYmRange(fromYm, toYm) {
    const from = Number(fromYm);
    const to = Number(toYm);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
    if (from <= to) return { fromYm: from, toYm: to };
    return { fromYm: to, toYm: from };
}

/** fromYm ~ toYm 포함 개월 수 */
export function ymInclusiveMonthCount(fromYm, toYm) {
    const range = normalizeYmRange(fromYm, toYm);
    if (!range) return 0;
    const fy = Math.floor(range.fromYm / 100);
    const fm = range.fromYm % 100;
    const ty = Math.floor(range.toYm / 100);
    const tm = range.toYm % 100;
    return Math.max(0, (ty - fy) * 12 + (tm - fm) + 1);
}

/** 기간 전체를 deltaMonths 만큼 이동 */
export function shiftYmRange(range, deltaMonths) {
    if (!range) return null;
    return {
        fromYm: ymAddMonths(Number(range.fromYm), deltaMonths),
        toYm: ymAddMonths(Number(range.toYm), deltaMonths),
    };
}

/**
 * 기준기간 바로 직전의 동일 길이 기간
 * 예: 202505~202507 -> 202502~202504
 */
export function previousPeriodYmRange(fromYm, toYm) {
    const range = normalizeYmRange(fromYm, toYm);
    if (!range) return null;
    const span = ymInclusiveMonthCount(range.fromYm, range.toYm);
    if (!span) return null;
    const prevToYm = ymAddMonths(range.fromYm, -1);
    const prevFromYm = ymAddMonths(prevToYm, -(span - 1));
    return { fromYm: prevFromYm, toYm: prevToYm };
}

/**
 * 마스터 화면에서 getCalcList 1회 호출용 YM union.
 * YEAR·MONTH 공통: 조회 연도의 1~12월(baseRange)만 요청. 전년·직전 12개월은 API에 합치지 않음.
 * momRange/yoyRange 는 추이 차트 등 비교 구간 메타용이며 union 과 다를 수 있음.
 * @returns {{ baseRange: { fromYm: number, toYm: number }, unionFrom: number, unionTo: number, spanMonths: number, momRange: object | null, yoyRange: object | null } | null}
 */
export function enIntMasterCalcListUnion(dateType, dateValue) {
    if (!isValidDatePeriod(dateType, dateValue)) return null;
    const baseRange = calcListBoundsFromDatePeriod(dateType, dateValue);
    if (!baseRange) return null;
    const spanMonths = ymInclusiveMonthCount(baseRange.fromYm, baseRange.toYm);
    const momRange = previousPeriodYmRange(baseRange.fromYm, baseRange.toYm);
    const yoyRange = shiftYmRange(baseRange, -12);
    return {
        baseRange,
        unionFrom: baseRange.fromYm,
        unionTo: baseRange.toYm,
        spanMonths,
        momRange,
        yoyRange,
    };
}

export function currentYm() {
    const d = new Date();
    return d.getFullYear() * 100 + d.getMonth() + 1;
}

/** 최근 12개월(당월 포함) fromYm ~ toYm */
export function last12MonthsYmRange() {
    const toYm = currentYm();
    const fromYm = ymAddMonths(toYm, -11);
    return { fromYm, toYm };
}

/** 차트용 짧은 월 라벨 예: 25.01 */
export function ymShortLabel(ym) {
    if (ym == null || ym === "") return "";
    const s = String(ym);
    if (s.length !== 6) return s;
    return `${s.slice(2, 4)}.${s.slice(4, 6)}`;
}

/** regYm당 calcIdx가 가장 큰 행만 남김 (월별 최신 계산) */
export function pickLatestCalcPerMonth(rows) {
    const map = new Map();
    for (const r of rows || []) {
        const ym = Number(r.regYm);
        if (!Number.isFinite(ym)) continue;
        const prev = map.get(ym);
        if (!prev || Number(r.calcIdx) > Number(prev.calcIdx)) map.set(ym, r);
    }
    return map;
}

/**
 * getCalcList 행 1건 → 브릿지·입력 비교가 쓰는 월별 시리즈 객체 형태.
 * mom.prevVal / yoy.yoyVal 은 서버 필드명(prevCalcVal 등)과 호환.
 * @returns {{ regYm: number, value: *, calcVal: *, calcStatus: *, mom?: object, yoy?: object } | null}
 */
export function monthlySeriesMonthFromCalcRow(r) {
    if (!r) return null;
    const ym = Number(r.regYm);
    if (!Number.isFinite(ym)) return null;
    const mom = r.mom;
    const yoy = r.yoy;
    return {
        regYm: ym,
        value: r.calcVal,
        calcVal: r.calcVal,
        calcStatus: r.calcStatus,
        mom: mom
            ? {
                  prevYm: mom.prevYm,
                  prevVal: mom.prevCalcVal ?? mom.prevVal,
                  rate: mom.rate,
                  delta: mom.delta,
              }
            : undefined,
        yoy: yoy
            ? {
                  yoyYm: yoy.yoyYm,
                  yoyVal: yoy.yoyCalcVal ?? yoy.yoyVal,
                  rate: yoy.rate,
                  delta: yoy.delta,
              }
            : undefined,
    };
}

/** getCalcList rows → 브릿지 바·박스가 기대하는 data.months (regYm 오름차순) */
export function bridgeChartMonthsFromCalcRows(rows) {
    const latestByYm = pickLatestCalcPerMonth(rows);
    const list = [];
    for (const row of latestByYm.values()) {
        const m = monthlySeriesMonthFromCalcRow(row);
        if (m) list.push(m);
    }
    list.sort((a, b) => a.regYm - b.regYm);
    return list;
}

/**
 * 카테고리별 Font Awesome 아이콘 클래스 (fas 기준 suffix)
 * AREA: 건물 (브릿지 패널은 icon-display fas fa-building 별도 처리)
 * HEADCOUNT: 재실, PROD: 제품
 */
export function categoryIconClass(category) {
    const c = String(category || "").toUpperCase();
    if (c === "AREA") return "fa-building";
    if (c === "HEADCOUNT") return "fa-users";
    if (c === "PROD") return "fa-box";
    return "fa-chart-line";
}

const STATUS = {
    OK: { label: "정상", cls: "ok" },
    NO_BASE: { label: "기준 관제점 값 없음", cls: "warn" },
    NO_REF: { label: "대상 관제점 값 없음", cls: "warn" },
    DIV_ZERO: { label: "기준값 0", cls: "danger" },
    ERROR: { label: "오류", cls: "danger" },
};

/**
 * calc 행 배지 (표시 문구·스타일 클래스).
 * `calcStatus === OK` → "정상", 그 외에는 `calcMsg`가 있으면 그 문구·없으면 상태별 기본 문구, `cls`는 상태 코드에 따름(미등록 시 muted).
 * @param {string|null|undefined} status calcStatus
 * @param {string|null|undefined} calcMsg
 * @returns {{ label: string, cls: string }}
 */
export function calcStatusBadge(status, calcMsg) {
    const s = String(status ?? "")
        .trim()
        .toUpperCase();
    if (s === "OK") return { ...STATUS.OK };

    const base = STATUS[s] || { label: s || "—", cls: "muted" };
    const msg = String(calcMsg ?? "").trim();
    return { label: msg || base.label, cls: base.cls };
}

/** ECharts 툴팁 DOM 클래스 (appendToBody 시 전역 스타일 대상) */
export const EN_INT_BRIDGE_TOOLTIP_CLASS = "ei-bridge-echarts-tooltip";

export function enIntBridgeTooltipEscape(text) {
    return String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * 브리지 바·박스 차트 공통 툴팁 베이스 (appendToBody + HTML 카드)
 * @param {Record<string, unknown>} partial trigger·axisPointer·formatter 등 병합
 */
export function enIntBridgeTooltipBase(partial = {}) {
    return {
        appendToBody: true,
        confine: false,
        className: EN_INT_BRIDGE_TOOLTIP_CLASS,
        renderMode: "html",
        borderWidth: 0,
        padding: 0,
        backgroundColor: "transparent",
        extraCssText: "box-shadow:none;border:none;background:transparent;max-width:min(280px,92vw);",
        transitionDuration: 0.18,
        ...partial,
    };
}

/**
 * @param {{ heading: string, primary?: string, muted?: string, rows?: { label: string, value: string }[], cardClass?: string }} p
 */
export function enIntBridgeTooltipCardHtml(p) {
    const h = enIntBridgeTooltipEscape(p.heading || "");
    const cardCls = ["ei-bridge-tooltip-card"];
    if (p.cardClass && String(p.cardClass).trim()) {
        cardCls.push(String(p.cardClass).trim());
    }
    let html = `<div class="${cardCls.join(" ")}"><div class="ei-bridge-tooltip-heading">${h}</div>`;
    if (p.muted != null && p.muted !== "") {
        html += `<div class="ei-bridge-tooltip-muted">${enIntBridgeTooltipEscape(p.muted)}</div>`;
    } else if (p.primary != null && p.primary !== "") {
        html += `<div class="ei-bridge-tooltip-primary">${enIntBridgeTooltipEscape(p.primary)}</div>`;
    }
    if (p.rows && p.rows.length) {
        html += `<div class="ei-bridge-tooltip-rows">`;
        for (const r of p.rows) {
            html += `<div class="ei-bridge-tooltip-kv"><span class="ei-bridge-tooltip-k">${enIntBridgeTooltipEscape(r.label)}</span><span class="ei-bridge-tooltip-v">${enIntBridgeTooltipEscape(r.value)}</span></div>`;
        }
        html += `</div>`;
    }
    html += `</div>`;
    return html;
}
